/**
 * Skeleton-wide defaults applied when a tenant doesn't supply a value
 * for an optional field. The skeleton imports `mergeWithDefaults` and
 * passes the result to its providers, so every consumer can assume
 * every field is present.
 */
import type {
  ComponentOverrides,
  CopyOverrides,
  ResolvedTenantBundle,
  TenantBundle,
  TenantChrome,
} from "./types";

const DEFAULT_COPY: CopyOverrides = {
  footerHeadline: "Insurance that works for your community.",
  heroHeadline: "Insurance, simplified.",
  heroSubheadline: "Get a quote in minutes.",
  ctaPrimary: "Get a quote",
  quoteSuccessTitle: "Your quote is ready",
  referralBlurb:
    "Tell friends about us — every successful referral earns a reward. " +
    "Override this in your tenant's CopyOverrides.",
  cookieConsentBody:
    "We use cookies to improve your experience. " +
    "Override this in your tenant's CopyOverrides.",
  policyWordingDocumentName: "Policy Wording",
  policyWordingFileName: "policy_wording.pdf",
};

const DEFAULT_COMPONENTS: Required<ComponentOverrides> = {
  // The skeleton renders its own Hero / Footer when these are undefined.
  // `as never` keeps the type happy without forcing a real component here
  // — consumers should narrow with `typeof X === "function"` before calling.
  Hero: undefined as never,
  Footer: undefined as never,
};

// Every piece of shared chrome is on unless a tenant opts out, so tenants
// written before `config.chrome` existed behave exactly as they did.
const DEFAULT_CHROME: Required<TenantChrome> = {
  landingNavbar: true,
  login: true,
  siteMenu: true,
  loginHref: "/dashboard",
};

/**
 * Merge a tenant's partial overrides over the skeleton-wide defaults.
 * Cheap, allocation-light — runs once at app boot.
 */
export function mergeWithDefaults(bundle: TenantBundle): ResolvedTenantBundle {
  return {
    ...bundle,
    copy: { ...DEFAULT_COPY, ...(bundle.copy ?? {}) },
    components: { ...DEFAULT_COMPONENTS, ...(bundle.components ?? {}) },
    pageBuilder: {
      allowedSections: bundle.pageBuilder?.allowedSections,
      defaults: { ...(bundle.pageBuilder?.defaults ?? {}) },
    },
    config: {
      ...bundle.config,
      chrome: { ...DEFAULT_CHROME, ...(bundle.config?.chrome ?? {}) },
    },
  };
}

/**
 * Sanity-check a tenant bundle at app boot. Throws when required fields
 * are blank — better to fail loud at the build than to ship a tenant
 * with an empty support email.
 */
export function assertValidBundle(bundle: TenantBundle): void {
  const required: Array<[string, string]> = [
    ["meta.code", bundle.meta?.code],
    ["meta.name", bundle.meta?.name],
    ["meta.country", bundle.meta?.country],
    ["meta.locale", bundle.meta?.locale],
    ["brand.appTitle", bundle.brand?.appTitle],
    ["brand.logoSrc", bundle.brand?.logoSrc],
    ["brand.supportEmail", bundle.brand?.supportEmail],
    ["brand.legalEntityName", bundle.brand?.legalEntityName],
  ];
  const missing = required.filter(([, v]) => !v || !String(v).trim()).map(([k]) => k);
  if (missing.length) {
    throw new Error(
      `TenantBundle for "${bundle.meta?.code ?? "<unknown>"}" is missing ` +
        `required fields: ${missing.join(", ")}`,
    );
  }
}
